import { fleetApi } from '../api/axiosClient';
import dayjs from 'dayjs';
import { dashboardCache } from './retaseStore';
import { timezone } from '../utils/timeZone';

const getTzParam = (tz) => {
  return tz ?? (typeof timezone === 'function' ? timezone() : Number(timezone) || 7);
};

const formatDateTime = (dateInput, timeBoundary, formatType = 'standard', tz = timezone) => {
  let d = dayjs(dateInput);

  const isDateOnly = typeof dateInput === 'string' && dateInput.length <= 10;

  if (isDateOnly && timeBoundary) {
    if (timeBoundary === 'start') {
      d = d.startOf('day');
    } else {
      d = d.endOf('day');
    }
  }

  const tzVal = typeof tz === 'function' ? tz() : Number(tz) || 7;
  // Mengurangi jam sesuai zona waktu (tz) karena backend UTC jika belum ISO UTC
  const isAlreadyUtcIso = typeof dateInput === 'string' && dateInput.endsWith('Z');
  if (!isAlreadyUtcIso) {
    d = d.subtract(tzVal, 'hour');
  }

  if (formatType === 'iso') {
    if (timeBoundary === 'start') {
      return d.format('YYYY-MM-DDTHH:mm:00.000[Z]');
    } else if (timeBoundary === 'end') {
      return d.format('YYYY-MM-DDTHH:mm:00.009[Z]'); // user wants .009Z for some reason?
    }
    return d.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  }

  const dateObj = d.toDate();

  const pad = (n) => (n < 10 ? '0' + n : n);
  const year = dateObj.getUTCFullYear();
  const monthIdx = dateObj.getUTCMonth();
  const month = pad(monthIdx + 1);
  const day = pad(dateObj.getUTCDate());
  const hours = pad(dateObj.getUTCHours());
  const minutes = pad(dateObj.getUTCMinutes());
  const seconds = pad(dateObj.getUTCSeconds());

  if (formatType === 'trip') {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day} ${monthNames[monthIdx]} ${year} ${hours}:${minutes}:${seconds}`;
  }

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const fetcherDataReportTrip = async (vehicleId, startDate, endDate, companyId, tz = timezone) => {
  const tzVal = getTzParam(tz);
  const sTime = formatDateTime(startDate, 'start', 'iso', tzVal);
  const eTime = formatDateTime(endDate, 'end', 'iso', tzVal);

  const cacheKey = `report_trip_${companyId}_${sTime}_${eTime}`;

  // 1. Check if data is already in cache
  const existingCache = dashboardCache.get(cacheKey) || {};
  if (existingCache[vehicleId]) {
    return existingCache[vehicleId];
  }

  try {
    const response = await fleetApi.get(
      `/report/all-sensor?tz=${tzVal}&companyId=${companyId}&vehicleId=${vehicleId}&startTime=${sTime}&endTime=${eTime}`
    );
    const data = response.data;

    // 2. Re-read cache to avoid race conditions with parallel Promise.all requests
    const updatedCache = dashboardCache.get(cacheKey) || {};
    updatedCache[vehicleId] = data;
    dashboardCache.set(cacheKey, updatedCache);

    return data;
  } catch (error) {
    if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
      console.warn(`[reportStore] fetcherDataReportTrip info for vehicle ${vehicleId}:`, error?.message || error);
    }
    return null;
  }
};

export const fetchSpeedMonitor = async (
  companyId,
  vehicleId,
  startDate,
  endDate,
  speedType = 'Speed Daily',
  startIdx = 0,
  length = 100,
  tz = timezone
) => {
  const tzVal = getTzParam(tz);

  const sTime = typeof startDate === 'string' && startDate.endsWith('Z') ? startDate : formatDateTime(startDate, 'start', 'iso', tzVal);
  const eTime = typeof endDate === 'string' && endDate.endsWith('Z') ? endDate : formatDateTime(endDate, 'end', 'iso', tzVal);

  const params = new URLSearchParams({
    tz: String(tzVal),
    companyId: String(companyId),
    vehicleId: String(vehicleId),
    speedType: speedType,
    start: String(startIdx),
    length: String(length),
    column: 'wp_time',
    logic_operator: 'RANGE',
    value: sTime,
    value1: eTime,
    operator: 'AND'
  });

  try {
    const response = await fleetApi.get(`/report/monitoring-speed?${params.toString()}`);
    return response;
  } catch (error) {
    console.error('Error Fetch Speed Monitor:', error);
    return null;
  }
};

export const fetchWaypointReport = async (companyId, vehicleIds, startDate, endDate, tz = timezone) => {
  const tzVal = getTzParam(tz);
  const sTime = typeof startDate === 'string' && startDate.endsWith('Z') ? startDate : formatDateTime(startDate, 'start', 'iso', tzVal);
  const eTime = typeof endDate === 'string' && endDate.endsWith('Z') ? endDate : formatDateTime(endDate, 'end', 'iso', tzVal);

  let allData = [];
  const limit = 1000;

  const vehicles = String(vehicleIds).split(',').filter(Boolean);

  for (const vId of vehicles) {
    let startIdx = 0;
    let hasMore = true;

    while (hasMore) {
      const payload = {
        paging: { start: startIdx, length: limit },
        columns: [{ name: 'wp_time', logic_operator: 'RANGE', value: sTime, value1: eTime, operator: 'AND' }]
      };

      try {
        const response = await fleetApi.post(`/waypoint/search?tz=${tzVal}&vehicleId=${vId}&companyId=${companyId}`, payload);

        // Ekstraksi data dan total record secara robust (mencakup response axios & body json)
        const apiPayload = response?.data || {};
        const data = Array.isArray(apiPayload) ? apiPayload : Array.isArray(apiPayload.data) ? apiPayload.data : [];

        const recordsTotal =
          response?.recordsTotal ??
          response?.totalRecord ??
          apiPayload?.recordsTotal ??
          apiPayload?.totalRecord ??
          apiPayload?.total ??
          apiPayload?.recordsFiltered ??
          0;

        allData = allData.concat(data);
        startIdx += limit;

        if (startIdx >= recordsTotal || data.length === 0) {
          hasMore = false;
        }
      } catch (error) {
        console.error(`Error Fetch Waypoint Report for Vehicle ${vId}:`, error);
        hasMore = false;
      }
    }
  }

  return allData;
};

export const fetchVehicleEvidence = async (companyId, vehicleId, eventType, startDate, endDate, limit = 10, offset = 0, tz = timezone) => {
  const tzVal = getTzParam(tz);

  const params = new URLSearchParams({
    companyId: String(companyId),
    vehicleId: String(vehicleId),
    eventType: String(eventType),
    startTime: startDate,
    endTime: endDate,
    limit: String(limit),
    offset: String(offset),
    tz: String(tzVal)
  });

  try {
    const response = await fleetApi.get(`/vehicle-evidence-bytelematic?${params.toString()}`);
    return response;
  } catch (error) {
    console.error('Error Fetch Vehicle Evidence:', error);
    return null;
  }
};
